// CAND-07 (Redesign Group 8): what the toast says when the person was saved but a later step
// was not. The record exists, so "was not added" would be untrue and invite a second copy.

export interface AddFailures {
  readonly isCvFailed: boolean;
  readonly isDetailsFailed: boolean;
}

export function partialAddMessage(name: string, { isCvFailed, isDetailsFailed }: AddFailures): string | null {
  if (isCvFailed && isDetailsFailed) return `${name} was added, but the CV didn't upload and their other details weren't saved — add them from their profile`;
  if (isCvFailed) return `${name} was added, but the CV didn't upload — add it from their profile`;
  if (isDetailsFailed) return `${name} was added, but their other details weren't saved — add them from their profile`;
  return null;
}

/**
 * Create, then the CV, then the custom fields. Only a failed create is "not added" (it throws);
 * a later step that fails is reported, never thrown, because the person already exists.
 */
export async function addCandidateInSteps<TResult>(
  create: () => Promise<TResult>,
  uploadCv: ((result: TResult) => Promise<unknown>) | null,
  saveDetails: ((result: TResult) => Promise<unknown>) | null,
): Promise<{ readonly result: TResult; readonly failures: AddFailures; readonly problems: readonly unknown[] }> {
  const result = await create();
  const problems: unknown[] = [];
  const isCvFailed = uploadCv ? await didFail(() => uploadCv(result), problems) : false;
  const isDetailsFailed = saveDetails ? await didFail(() => saveDetails(result), problems) : false;
  return { result, failures: { isCvFailed, isDetailsFailed }, problems };
}

async function didFail(step: () => Promise<unknown>, problems: unknown[]): Promise<boolean> {
  try {
    await step();
    return false;
  } catch (cause) {
    problems.push(cause);
    return true;
  }
}
